'use client';

import { useEffect, useState } from 'react';
import { SWEETLINK_AUTO_KEY, SWEETLINK_AUTO_RESUME_FROM_KEY } from '@/lib/sweetlink/constants';
import { getBrowserWindow } from '@/lib/utils/browser-window';

const shouldAutoBootstrap = (browserWindow: Window): boolean => {
  try {
    const currentUrl = new URL(browserWindow.location.href);
    if (currentUrl.searchParams.get('sweetlink') === 'auto') {
      return true;
    }
  } catch {
    // ignore URL parsing failures
  }

  try {
    const stored = browserWindow.sessionStorage?.getItem(SWEETLINK_AUTO_KEY);
    if (stored === 'pending' || stored === 'resume') {
      return true;
    }
    const resumeTarget = browserWindow.sessionStorage?.getItem(SWEETLINK_AUTO_RESUME_FROM_KEY);
    return Boolean(resumeTarget);
  } catch {
    return false;
  }
};

export function SweetLinkCliBootstrapScript() {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const browserWindow = getBrowserWindow();
    if (!browserWindow) {
      return;
    }
    if (shouldAutoBootstrap(browserWindow)) {
      setShouldLoad(true);
      return;
    }

    // SweetLink CLI can set sessionStorage after hydration; poll briefly so we can enable auto bootstrap.
    const pollIntervalMs = 200;
    const pollTimeoutMs = 15_000;
    const pollId = browserWindow.setInterval(() => {
      if (shouldAutoBootstrap(browserWindow)) {
        browserWindow.clearInterval(pollId);
        browserWindow.clearTimeout(timeoutId);
        setShouldLoad(true);
      }
    }, pollIntervalMs);
    const timeoutId = browserWindow.setTimeout(() => {
      browserWindow.clearInterval(pollId);
    }, pollTimeoutMs);

    return () => {
      browserWindow.clearInterval(pollId);
      browserWindow.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!shouldLoad) {
      return;
    }
    let cancelled = false;
    const loadScript = async () => {
      try {
        const bootstrapModule = await import('@/client/sweetlink/cli-bootstrap-script');
        if (!cancelled) {
          bootstrapModule.runSweetLinkCliBootstrap();
        }
      } catch {
        // ignore load failures; SweetLink auto bootstrap will stay disabled
      }
    };
    loadScript().catch(() => {
      // loadScript owns error handling
    });
    return () => {
      cancelled = true;
    };
  }, [shouldLoad]);

  return null;
}
